import { useEffect } from 'react'

import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { CampaignPlatformTypeEnum } from 'src/Types/campaigns'
import {
  TiktokTrack,
  Track,
  useGetFirstTikTokTrackQuery,
} from 'src/Redux/firstTrackApi'
import { create } from 'src/Router/routes'
import {
  SpotifyTrack,
  useGetSpotifyTrackByIdQuery,
} from 'src/Redux/spotifyTracksApi'
import { convertTimeHandler } from 'src/Constants/functions'

export interface FirstChosenTrackParams {
  [key: string]: string | undefined
  platform?: CampaignPlatformTypeEnum
  trackId?: string
}

type UseFirstChosenTrack = {
  track: {
    spotifyTrack?: SpotifyTrack
    tiktokTrack?: TiktokTrack
  }
  unifyTrack: Track | null
  loading?: boolean
  isSuccess?: boolean
  platform: CampaignPlatformTypeEnum | undefined
}

const MILLISECONDS_PER_SECOND = 1000

const getUnifyTrack = (
  spotifyTrack?: SpotifyTrack,
  tiktokTrack?: TiktokTrack,
): Track | null => {
  if (spotifyTrack) {
    return {
      trackName: spotifyTrack.trackName,
      trackId: spotifyTrack.id,
      artistName: spotifyTrack.artists?.map((artist) => artist.name).join(', '),
      trackDurationInMs: spotifyTrack.trackDuration,
      trackDuration: convertTimeHandler({
        value: spotifyTrack?.trackDuration || 0,
        type: 'ms',
      }),
      trackCoverUrl: spotifyTrack.trackImage,
      trackPreviewUrl: spotifyTrack.previewUrl,
    }
  }
  if (tiktokTrack) {
    return {
      trackName: tiktokTrack.trackTitle,
      trackId: tiktokTrack.trackTiktokId,
      artistName: tiktokTrack.trackAuthorName,
      trackDurationInMs:
        (tiktokTrack?.trackDuration || 0) * MILLISECONDS_PER_SECOND,
      trackDuration: convertTimeHandler({
        value: tiktokTrack?.trackDuration || 0,
        type: 's',
      }),
      trackCoverUrl: tiktokTrack.trackCoverUrl,
      trackPreviewUrl: tiktokTrack.trackPlayUrl,
    }
  }
  return null
}

export const useFirstChosenTrack = (): UseFirstChosenTrack => {
  const { platform, trackId } = useParams<FirstChosenTrackParams>()

  const navigate = useNavigate()
  const { search } = useLocation()
  const {
    data: spotifyTrack,
    isLoading: isSpotifyLoading,
    isError: isSpotifyError,
    isSuccess: isSpotifySuccess,
  } = useGetSpotifyTrackByIdQuery(trackId || '', {
    skip: !trackId || platform !== CampaignPlatformTypeEnum.SPOTIFY,
  })
  const {
    data: tiktokTrack,
    isLoading: isTiktokLoading,
    isError: isTiktokError,
    isSuccess: isTiktokSuccess,
  } = useGetFirstTikTokTrackQuery(trackId || '', {
    skip: !trackId || platform !== CampaignPlatformTypeEnum.TIKTOK,
  })

  useEffect(() => {
    if (isSpotifyError || isTiktokError) {
      navigate({ pathname: `${create}/${platform}`, search }, { replace: true })
    }
  }, [isSpotifyError, isTiktokError, navigate, platform, search])

  return {
    platform,
    track: { spotifyTrack, tiktokTrack },
    unifyTrack: getUnifyTrack(spotifyTrack, tiktokTrack),
    isSuccess: isSpotifySuccess || isTiktokSuccess,
    loading: isSpotifyLoading || isTiktokLoading,
  }
}
